"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  BarChart3,
  PartyPopperIcon,
  SearchIcon,
  Users,
  MessageSquareIcon,
  Bell,
  ChevronUp,
  Settings,
  LogOut,
  Mail,
  CircleHelp,
} from "lucide-react";
import { useState, type ComponentProps } from "react";
import { useUser, useClerk } from "@clerk/nextjs";
import { useTranslations } from "next-intl";
import { AdminNavDropdown } from "./admin-nav-dropdown";
import { BrandMark } from "./brand-mark";
import { GetAppMenuItem } from "./install-app/get-app-menu-item";
import { LanguageSwitcher } from "./language-switcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { HowItWorksDialog } from "./how-it-works-dialog";
import { useDisplayedAppVersion } from "@award-party/shared";
import { formatAppVersion } from "@/lib/app-version";

const mainLinkKeys = [
  { href: "/parties", labelKey: "myParties", icon: PartyPopperIcon },
  { href: "/friends", labelKey: "friends", icon: Users },
  { href: "/award-shows", labelKey: "browseShows", icon: SearchIcon },
  { href: "/predictions", labelKey: "predictions", icon: BarChart3 },
] as const;

const helpLinkKeys = [
  { href: "/contact", labelKey: "contactUs", icon: Mail },
  { href: "/feedback", labelKey: "sendFeedback", icon: MessageSquareIcon },
] as const;

function SidebarDropdownMenu(props: ComponentProps<typeof DropdownMenu>) {
  return <DropdownMenu modal={false} {...props} />;
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  const last = parts[parts.length - 1];
  if (!first) {
    return "?";
  }
  if (!last || parts.length === 1) {
    return first.charAt(0).toUpperCase();
  }
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

function SidebarUser({ onNavigate }: { onNavigate?: () => void }) {
  const t = useTranslations("Sidebar");
  const { user } = useUser();
  const { signOut, openUserProfile } = useClerk();

  const displayName =
    user?.fullName ||
    user?.username ||
    user?.primaryEmailAddress?.emailAddress ||
    t("accountFallback");
  const avatarUrl = user?.imageUrl;
  const initials = initialsFromName(displayName);

  const handleManageAccount = () => {
    if (onNavigate) {
      onNavigate();
      setTimeout(() => openUserProfile(), 300);
    } else {
      openUserProfile();
    }
  };

  return (
    <div className="shrink-0 border-t border-(--mk-cream-08) px-3 py-3">
      <SidebarDropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex w-full cursor-pointer items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-(--mk-cream-surface) data-[state=open]:bg-(--mk-cream-surface)"
          >
            {avatarUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={avatarUrl}
                alt=""
                className="h-8 w-8 shrink-0 rounded-full object-cover"
              />
            ) : (
              <div
                aria-hidden="true"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-(--mk-gold) text-xs font-bold text-(--mk-ink)"
              >
                {initials}
              </div>
            )}
            <span
              className="min-w-0 flex-1 truncate text-left text-sm font-medium text-(--mk-cream)"
              title={displayName}
            >
              {displayName}
            </span>
            <ChevronUp className="h-4 w-4 shrink-0 text-(--mk-cream-35) transition-transform [[data-state=open]_&]:rotate-180" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="top"
          align="start"
          sideOffset={8}
          className="w-(--radix-dropdown-menu-trigger-width) border-(--mk-cream-08) bg-[#1c1914] p-1.5 text-(--mk-cream)"
        >
          <DropdownMenuItem asChild onSelect={() => onNavigate?.()}>
            <Link
              href="/settings/notifications"
              onClick={onNavigate}
              className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm text-(--mk-cream-60) hover:text-(--mk-cream)"
            >
              <Bell className="h-4 w-4 shrink-0 text-(--mk-gold)" />
              {t("notifications")}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <button
              type="button"
              onClick={handleManageAccount}
              className="flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm text-(--mk-cream-60) hover:text-(--mk-cream)"
            >
              <Settings className="h-4 w-4 shrink-0 text-(--mk-gold)" />
              {t("manageAccount")}
            </button>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <button
              type="button"
              onClick={() => signOut()}
              className="flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm text-(--mk-cream-60) hover:text-(--mk-cream)"
            >
              <LogOut className="h-4 w-4 shrink-0 text-(--mk-gold)" />
              {t("signOut")}
            </button>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </SidebarDropdownMenu>
    </div>
  );
}

function SidebarHelpMenu({
  onNavigate,
  onHowItWorks,
}: {
  onNavigate?: () => void;
  onHowItWorks: () => void;
}) {
  const t = useTranslations("Sidebar");
  const tCommon = useTranslations("Common");
  const version = useDisplayedAppVersion();

  return (
    <SidebarDropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex cursor-pointer items-center gap-1 rounded-md px-1.5 py-1.5 text-xs font-medium text-(--mk-cream-60) transition-colors hover:bg-(--mk-cream-surface) hover:text-(--mk-cream) data-[state=open]:bg-(--mk-cream-surface) data-[state=open]:text-(--mk-cream)"
        >
          <CircleHelp className="h-3.5 w-3.5 shrink-0" />
          {t("help")}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="start"
        sideOffset={8}
        className="w-56 border-(--mk-cream-08) bg-[#1c1914] p-1.5 text-(--mk-cream)"
      >
        <DropdownMenuItem asChild>
          <button
            type="button"
            onClick={onHowItWorks}
            className="flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm text-(--mk-cream-60) hover:text-(--mk-cream)"
          >
            <CircleHelp className="h-4 w-4 shrink-0 text-(--mk-gold)" />
            {t("howItWorks")}
          </button>
        </DropdownMenuItem>
        {helpLinkKeys.map(({ href, labelKey, icon: Icon }) => (
          <DropdownMenuItem key={href} asChild onSelect={() => onNavigate?.()}>
            <Link
              href={href}
              onClick={onNavigate}
              className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm text-(--mk-cream-60) hover:text-(--mk-cream)"
            >
              <Icon className="h-4 w-4 shrink-0 text-(--mk-gold)" />
              {t(labelKey)}
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="bg-(--mk-cream-08)" />
        <DropdownMenuLabel className="flex items-center justify-between px-2 py-1.5 text-[11px] font-normal text-(--mk-cream-35)">
          <span>{tCommon("brand")}</span>
          <span>{formatAppVersion(version)}</span>
        </DropdownMenuLabel>
      </DropdownMenuContent>
    </SidebarDropdownMenu>
  );
}

interface SidebarContentProps {
  isAdmin: boolean;
  onNavigate?: () => void;
}

export function SidebarContent({ isAdmin, onNavigate }: SidebarContentProps) {
  const t = useTranslations("Sidebar");
  const tCommon = useTranslations("Common");
  const pathname = usePathname();
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);

  const isActive = (href: string) =>
    href === "/parties"
      ? pathname === "/parties" || pathname.startsWith("/parties/")
      : pathname === href || pathname.startsWith(href + "/");

  const linkClass = (href: string) =>
    `flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2.5 text-sm font-medium transition-all ${
      isActive(href)
        ? "border-(--mk-gold-border) bg-(--mk-gold-bg) text-(--mk-gold-light)"
        : "border-transparent text-(--mk-cream-60) hover:bg-(--mk-cream-surface) hover:text-(--mk-cream)"
    }`;

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <div className="flex h-16 shrink-0 items-center gap-2.5 border-b border-(--mk-cream-08) px-5">
        <Link
          href="/parties"
          className="flex items-center gap-2.5 no-underline"
          onClick={onNavigate}
          data-app-chrome-mark=""
        >
          <BrandMark size={36} className="h-9 w-9" />
          <span className="font-serif text-xl font-bold tracking-tight text-(--mk-cream)">
            {tCommon("brand")}
          </span>
        </Link>
      </div>

      <nav className="flex flex-1 flex-col px-3 py-6">
        <div className="flex flex-col gap-1">
          {mainLinkKeys.map(({ href, labelKey, icon: Icon }) => (
            <Link
              key={href}
              href={href}
              className={linkClass(href)}
              onClick={onNavigate}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {t(labelKey)}
            </Link>
          ))}
        </div>

        <div className="mt-auto flex flex-col gap-3">
          {isAdmin ? (
            <AdminNavDropdown variant="sidebar" onNavigate={onNavigate} />
          ) : null}
          <div className="flex items-center gap-1 px-0.5">
            <SidebarHelpMenu
              onNavigate={onNavigate}
              onHowItWorks={() => setHowItWorksOpen(true)}
            />
            <GetAppMenuItem onNavigate={onNavigate} />
            <LanguageSwitcher variant="sidebar" />
          </div>
        </div>
      </nav>

      <SidebarUser onNavigate={onNavigate} />
      <HowItWorksDialog open={howItWorksOpen} onOpenChange={setHowItWorksOpen} />
    </div>
  );
}

export function AppSidebar({ isAdmin }: { isAdmin: boolean }) {
  return (
    <aside data-app-chrome="" className="fixed inset-y-0 left-0 z-40 hidden w-56 flex-col border-r border-(--mk-cream-08) bg-(--mk-ink) ap-pad-safe-top ap-pad-safe-bottom ap-pad-safe-left lg:flex">
      <SidebarContent isAdmin={isAdmin} />
    </aside>
  );
}
